import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import logSaveClicked from '@alltrails/amplitude/events/logSaveClicked';
import useUser from '@alltrails/context/hooks/useUser';
import IconButton from '@alltrails/denali/components/IconButton';
import Heart from '@alltrails/denali/icons/Heart';
import HeartFilled from '@alltrails/denali/icons/HeartFilled';
import ListSignUpModal from '@alltrails/lists/components/ListSignUpModal';
import styles from './TopResults.module.scss';

if (typeof window !== 'undefined' && 'Cypress' in window) {
  const handleWindowLoad = () => {
    console.log(`[HeartButtonWrapper] window load at ${Math.round(performance.now())}ms`, { readyState: document.readyState });
  };

  if (document.readyState === 'complete') {
    handleWindowLoad();
  } else {
    window.addEventListener('load', handleWindowLoad);
  }
}

type Props = {
  isFavorite: boolean;
  trailId: number;
  onSave: () => void;
};

const HeartButtonWrapper = ({ isFavorite, trailId, onSave }: Props) => {
  const intl = useIntl();
  const user = useUser();
  const [isSignUpOpen, setIsSignUpOpen] = useState(false);
  const [hasSignedUpToSave, setHasSignedUpToSave] = useState(false);
  const didResumeSaveRef = useRef(false);

  useEffect(() => {
    if (!hasSignedUpToSave || !user || didResumeSaveRef.current) {
      return;
    }
    didResumeSaveRef.current = true;
    onSave();
  }, [hasSignedUpToSave, onSave, user]);

  useEffect(() => {
    if ('Cypress' in window) {
      console.log(`[HeartButtonWrapper] mount at ${Math.round(performance.now())}ms`, { isFavorite, readyState: document.readyState });
    }
  }, [isFavorite]);

  useEffect(() => {
    if ('Cypress' in window) {
      console.log(`[HeartButtonWrapper] isSignUpOpen at ${Math.round(performance.now())}ms`, {
        isSignUpOpen,
        isFavorite,
        readyState: document.readyState
      });
    }
  }, [isSignUpOpen, isFavorite]);

  const handleClick = () => {
    if ('Cypress' in window) {
      console.log(`[HeartButtonWrapper] click at ${Math.round(performance.now())}ms`, { isFavorite, readyState: document.readyState });
    }

    // Logged before the sign up branch so the event still covers signed out presses.
    logSaveClicked({
      detailed_card_location: 'location page list',
      save_location: 'location page',
      map_id: trailId,
      map_type: 'trail'
    });

    if (!user) {
      didResumeSaveRef.current = false;
      setIsSignUpOpen(true);
      return;
    }

    onSave();
  };

  return (
    <>
      <IconButton
        className={styles.trailCardSnippetHeart}
        icon={isFavorite ? { Component: HeartFilled } : { Component: Heart }}
        onClick={handleClick}
        size="sm"
        title={intl.formatMessage({ defaultMessage: 'Add to list' })}
      />
      <ListSignUpModal
        isOpen={isSignUpOpen}
        item={{ id: trailId, type: 'trail' }}
        onClose={() => setIsSignUpOpen(false)}
        onSuccess={() => setHasSignedUpToSave(true)}
      />
    </>
  );
};

export default HeartButtonWrapper;
